import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {findMatchingPolicyStatement} from 'auth/PermissionUtil';
import {Condition, PolicyStatement} from 'auth/PolicyConstants';
import {ConditionOptionNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

import {ActionItemType, ActionRowData} from './getActionRowDatas';

interface Props {
  isWritable: boolean;
  policyStatements: Array<PolicyStatement>;
  actionRowData: ActionRowData;
  onUpdateStatements: (policyStatements: Array<PolicyStatement>) => void;
}

/**
 * Return the condition display name, based on how the policy statements are configured.
 */
export function getConditionDisplayName(
  policyStatements: Array<PolicyStatement>,
  actionItemTypes: Array<ActionItemType>,
  conditionOptions: Array<ConditionOptionNode>
): string {
  const conditionOptionsByName = _.indexBy(conditionOptions, 'condition');
  const matchingConditions = actionItemTypes.map((actionItemType: ActionItemType) => {
    const policyStatement = findMatchingPolicyStatement(policyStatements, actionItemType);
    if (!policyStatement) {
      return null;
    }
    return policyStatement.condition;
  });
  const uniqueConditions = _.unique(matchingConditions);
  if (uniqueConditions.length === 1) {
    const condition = uniqueConditions[0];
    if (!condition) {
      return 'Not granted';
    }
    if (
      assertAndContinue(
        Boolean(conditionOptionsByName[condition]),
        'Expected condition to exist in policyTemplate.json'
      )
    ) {
      return conditionOptionsByName[condition].name;
    }
    // Gracefully fallback to the condition, so the page doesn't crash.
    return condition;
  }
  return 'Mixed permissions';
}

class ConditionDropdown extends React.Component<Props> {
  _handleSelectCondition = (condition: Condition | null): void => {
    // Selecting a condition (or lack of condition, "Not Granted") should apply that condition
    // to all action / item type pairs that this component applies to.
    // This function removes all policy statements for the applicable action / item types, and
    // applies the selected condition (if specified) for those action / item types.
    const newStatements = this.props.policyStatements.filter(
      (policyStatement) =>
        !this.props.actionRowData.actionItemTypes.some(
          (actionItemType) =>
            actionItemType.action === policyStatement.action &&
            actionItemType.itemType === policyStatement.itemType
        )
    );
    if (condition) {
      for (const actionItemType of this.props.actionRowData.actionItemTypes) {
        newStatements.push({
          action: actionItemType.action,
          itemType: actionItemType.itemType,
          condition,
        });
      }
    }
    this.props.onUpdateStatements(newStatements);
  };
  render(): JSX.Element {
    return (
      <SelectDropdown
        displayName={getConditionDisplayName(
          this.props.policyStatements,
          this.props.actionRowData.actionItemTypes,
          this.props.actionRowData.conditionOptions
        )}
        disabled={!this.props.isWritable || this.props.actionRowData.disabled}
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={2}>
            <Section>
              <Option<Condition | null> value={null} onSelect={this._handleSelectCondition}>
                Not granted
              </Option>
            </Section>
            <Section>
              {this.props.actionRowData.conditionOptions.map((conditionOption) => (
                <Option
                  key={conditionOption.condition}
                  onSelect={this._handleSelectCondition}
                  value={conditionOption.condition}
                >
                  {conditionOption.name}
                </Option>
              ))}
            </Section>
          </Menu>
        }
      />
    );
  }
}

export default ConditionDropdown;
